import { TextField, Box, Button, IconButton } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useState, useContext } from "react";

import { ChatContext } from "../../contexts/chat/context";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";

export const InputContainer = () => {
  const {
    sendMessage,
    stopCurrentResponse,
    isAwaitingResponse,
    agentState,
    isStopRequested,
    transportCapabilities,
    transportKind,
    isConnected,
    isConnecting,
    platformEarlierMessagesLoading = false,
    queuedPromptItems = [],
    cancelQueuedPrompt,
    queuedDispatchPaused = false,
    queuedDispatchPauseMessage = null,
    queuedDispatchCanRetry = false,
    retryQueuedDispatch,
  } = useContext(ChatContext);
  const [inputValue, setInputValue] = useState("");

  // Only transports that advertise queueing may accept another prompt while busy.
  const isResponseActive =
    isAwaitingResponse || agentState.status === "streaming";
  const isBusy = isResponseActive || queuedDispatchPaused;
  const transportReady =
    transportKind !== "platform" || (isConnected && !isConnecting);
  const canSend =
    transportReady &&
    !platformEarlierMessagesLoading &&
    !!inputValue.trim() &&
    (!isBusy || transportCapabilities.queue);
  const showQueueAction = isBusy && canSend;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    if (canSend) {
      sendMessage(inputValue);
      setInputValue("");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          width: "100%",
          minHeight: 50,
          py: 0.65,
          pl: 1.65,
          pr: 0.7,
          backgroundColor: "#ffffff",
          borderRadius: "26px",
          border: "1px solid",
          borderColor: "rgba(102, 116, 138, 0.24)",
          boxShadow: "0 8px 26px rgba(31, 39, 51, 0.08)",
          "&:focus-within": {
            borderColor: "#3877F7",
            boxShadow: "0 8px 28px rgba(56, 119, 247, 0.14)",
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={
            !transportReady
              ? isConnecting
                ? "Nucleus is checking availability..."
                : "Nucleus is unavailable. Retry the connection above."
              : platformEarlierMessagesLoading
                ? "Loading earlier messages..."
                : isBusy
                  ? queuedDispatchPaused
                    ? "Queue another question (dispatch is paused)..."
                    : transportCapabilities.queue
                      ? "Ask while Nucleus is working (it will queue)..."
                      : "Nucleus is responding..."
                  : "Ask Nucleus..."
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          inputProps={{ "aria-label": "Ask Nucleus" }}
          variant="standard"
          size="small"
          disabled={
            !transportReady ||
            platformEarlierMessagesLoading ||
            (isBusy && !transportCapabilities.queue)
          }
          sx={{
            "& .MuiInput-root": {
              alignItems: "flex-start",
              fontSize: "0.95rem",
              fontFamily: CHAT_FONT_STACK,
              "&:before, &:after": {
                display: "none",
              },
              "&.Mui-disabled": {
                color: "text.disabled",
              },
            },
            "& .MuiInputBase-input": {
              padding: "4px 4px",
              fontFamily: CHAT_FONT_STACK,
              "&::placeholder": {
                color: "text.secondary",
                opacity: 0.7,
              },
            },
          }}
        />
        {isResponseActive && !showQueueAction ? (
          <IconButton
            aria-label="Stop response"
            onClick={() => stopCurrentResponse(false)}
            disabled={isStopRequested}
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "#fff4f4",
              color: "#c62828",
              border: "1px solid #ef9a9a",
              "&:hover": {
                backgroundColor: "#ffeaea",
              },
              "&:focus-visible": {
                outline: "2px solid #1F2733",
                outlineOffset: 2,
              },
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "#bdbdbd",
                borderColor: "#e0e0e0",
              },
            }}
          >
            <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            aria-label={
              showQueueAction || queuedDispatchPaused
                ? "Queue question"
                : "Send message"
            }
            onClick={handleSendMessage}
            disabled={!canSend}
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: canSend ? "#3877F7" : "#e8edf5",
              color: "white",
              "&:hover": {
                backgroundColor: canSend ? "#2C65DB" : "#e8edf5",
              },
              "&:focus-visible": {
                outline: "2px solid #1F2733",
                outlineOffset: 2,
              },
              "&.Mui-disabled": {
                backgroundColor: "#e8edf5",
                color: "#b0bac8",
              },
            }}
          >
            <ArrowUpwardRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        )}
      </Box>
      {(queuedPromptItems.length > 0 || queuedDispatchPaused) && (
        <Box
          component="section"
          aria-label="Queued questions"
          sx={{
            mt: 0.8,
            display: "grid",
            gap: 0.55,
          }}
        >
          {queuedDispatchPaused && queuedDispatchPauseMessage && (
            <Box
              sx={{
                px: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box
                role="status"
                sx={{
                  minWidth: 0,
                  fontFamily: CHAT_FONT_STACK,
                  fontSize: "0.7rem",
                  lineHeight: 1.35,
                  fontWeight: 600,
                  color: "#8A5A12",
                }}
              >
                {queuedDispatchPauseMessage}
              </Box>
              {queuedDispatchCanRetry && retryQueuedDispatch && (
                <Button
                  size="small"
                  onClick={retryQueuedDispatch}
                  sx={{
                    minWidth: 0,
                    px: 0.8,
                    flexShrink: 0,
                    fontFamily: CHAT_FONT_STACK,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Retry queue
                </Button>
              )}
            </Box>
          )}
          {queuedPromptItems.length > 0 && (
            <Box
              role={queuedDispatchPaused ? undefined : "status"}
              sx={{
                px: 0.5,
                fontFamily: CHAT_FONT_STACK,
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#66748A",
              }}
            >
              {queuedPromptItems.length === 1
                ? "1 question queued"
                : `${queuedPromptItems.length} questions queued`}
            </Box>
          )}
          {queuedPromptItems.map((prompt, index) => (
            <Box
              key={prompt.id}
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.65,
                py: 0.45,
                pl: 1.1,
                pr: 0.45,
                borderRadius: "12px",
                border: "1px solid rgba(56, 119, 247, 0.15)",
                bgcolor: "rgba(56, 119, 247, 0.045)",
              }}
            >
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  fontFamily: CHAT_FONT_STACK,
                  fontSize: "0.66rem",
                  fontWeight: 700,
                  color: "#2C65DB",
                }}
              >
                {index === 0 ? "Next" : `Then ${index + 1}`}
              </Box>
              <Box
                component="span"
                title={prompt.text}
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontFamily: CHAT_FONT_STACK,
                  fontSize: "0.74rem",
                  color: "#38465A",
                }}
              >
                {prompt.text}
              </Box>
              <IconButton
                aria-label={`Remove queued question: ${prompt.text}`}
                onClick={() => cancelQueuedPrompt?.(prompt.id)}
                size="small"
                sx={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  color: "#66748A",
                  "&:focus-visible": {
                    outline: "2px solid #3877F7",
                    outlineOffset: 1,
                  },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
